/**
 * Authentication Context
 * Manages user session state globally using Supabase Auth
 */

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import {
  identifyUser,
  resetUser,
  track,
  getStoredUtmAttribution,
  clearStoredUtmAttribution,
  trackKlaviyoLoginOncePerSession,
} from '@/lib/analytics';

// ============================================================================
// Klaviyo signup sync
// ============================================================================

// Nur „frische" Nutzer beim ersten Login synchronisieren (schützt Bestands-
// nutzer vor ungewolltem Re-Subscribe). Fenster deckt die E-Mail-Bestätigungs-
// verzögerung ab (Supabase-Bestätigungslinks laufen i. d. R. nach 24 h ab).
const KLAVIYO_SIGNUP_SYNC_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // 7 Tage
const klaviyoSyncKey = (userId: string): string => `klaviyo_signup_synced_${userId}`;

/**
 * Meldet einen neuen Nutzer nach Klaviyo (Consent + Subscribe + „Signed Up"-Event)
 * — GENAU EINMAL pro Nutzer, per localStorage-Guard. Wird beim ersten
 * authentifizierten Login ausgelöst statt bei signUp(), damit auch
 * E-Mail-Bestätigungs-Signups erfasst werden (dort existiert bei signUp() noch
 * keine Session → der direkte invoke liefe ins 401). Idempotent: das Event ist
 * in Klaviyo per unique_id dedupliziert, Subscribe/Consent sind wiederholbar.
 * Der Guard wird nur bei Erfolg gesetzt → schlägt der Call fehl, versucht es der
 * nächste Login erneut.
 */
async function syncSignupToKlaviyo(userId: string): Promise<void> {
  const key = klaviyoSyncKey(userId);
  try {
    if (localStorage.getItem(key)) return;
  } catch {
    // localStorage kann blockiert sein (Privacy-Modus) — dann eben ohne Guard.
  }
  try {
    const { data, error } = await supabase.functions.invoke('klaviyo-sync-self', { body: { consent: true } });
    if (error) throw error;
    if (data && (data as { success?: boolean }).success === false) {
      throw new Error((data as { error?: string }).error || 'Klaviyo-Sync fehlgeschlagen');
    }
    try {
      localStorage.setItem(key, new Date().toISOString());
    } catch {
      // Guard nicht persistierbar — unkritisch (Event ist serverseitig dedupliziert).
    }
  } catch (e) {
    // Bewusst NICHT als synchronisiert markieren → Retry beim nächsten Login.
    console.warn('[Klaviyo] Signup-Sync fehlgeschlagen (Retry beim nächsten Login):', e);
  }
}

// ============================================================================
// Types
// ============================================================================

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  /**
   * True once `fetchProfileFlags` has resolved at least once (success or failure).
   * Use this — together with `isLoading` — to know when `isAdmin`/`isOnboarded` are
   * trustworthy, especially on page reload where AdminRoute can otherwise race-trigger
   * access-denied between session-load and profile-load.
   */
  profileFlagsLoaded: boolean;
  isAdmin: boolean;
  isOnboarded: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, referralCode?: string) => Promise<{ error: string | null }>;
  signInWithProvider: (provider: 'google') => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refreshAdminStatus: () => Promise<void>;
  refreshOnboardingStatus: () => Promise<void>;
  disabledFeatures: string[];
  refreshFeatureFlags: () => Promise<void>;
}

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================================================
// Provider
// ============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [profileFlagsLoaded, setProfileFlagsLoaded] = useState(false);
  const [isOnboarded, setIsOnboarded] = useState(true); // default true to avoid flash
  const [disabledFeatures, setDisabledFeatures] = useState<string[]>([]);

  // Update last_active_at on profiles (throttled to once per hour)
  const updateLastActive = useCallback(async (userId: string) => {
    const THROTTLE_KEY = 'ev_last_active_update';
    const THROTTLE_MS = 60 * 60 * 1000; // 1 hour
    const lastUpdate = localStorage.getItem(THROTTLE_KEY);
    if (lastUpdate && Date.now() - parseInt(lastUpdate, 10) < THROTTLE_MS) return;

    localStorage.setItem(THROTTLE_KEY, Date.now().toString());
    await supabase
      .from('profiles')
      .update({ last_active_at: new Date().toISOString() })
      .eq('id', userId);
  }, []);

  // Fetch global feature flags from app_settings
  const fetchAppSettings = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('disabled_features')
        .eq('id', 1)
        .single();
      if (!error) {
        setDisabledFeatures(data?.disabled_features ?? []);
      }
    } catch (err) {
      console.error('Error fetching app settings:', err);
    }
  }, []);

  // Refresh feature flags (can be called externally, e.g. after admin toggle)
  const refreshFeatureFlags = useCallback(async () => {
    await fetchAppSettings();
  }, [fetchAppSettings]);

  // Fetch profile flags (admin status from profiles, onboarding from user_onboarding)
  const fetchProfileFlags = useCallback(async (userId: string) => {
    try {
      const [profileResult, onboardingResult] = await Promise.all([
        supabase.from('profiles').select('is_admin').eq('id', userId).single(),
        supabase.from('user_onboarding').select('is_onboarded').eq('user_id', userId).single(),
      ]);

      if (profileResult.error) {
        console.error('Error fetching profile flags:', profileResult.error);
        setIsAdmin(false);
      } else {
        setIsAdmin(profileResult.data?.is_admin ?? false);
      }

      if (onboardingResult.error) {
        console.error('Error fetching onboarding flags:', onboardingResult.error);
        setIsOnboarded(true);
      } else {
        setIsOnboarded(onboardingResult.data?.is_onboarded ?? true);
      }
    } catch (err) {
      console.error('Error fetching profile flags:', err);
      setIsAdmin(false);
      setIsOnboarded(true);
    } finally {
      setProfileFlagsLoaded(true);
    }
  }, []);

  // Refresh admin status (can be called externally)
  const refreshAdminStatus = useCallback(async () => {
    if (user?.id) {
      await fetchProfileFlags(user.id);
    }
  }, [user?.id, fetchProfileFlags]);

  // Refresh onboarding status (called after wizard completion)
  const refreshOnboardingStatus = useCallback(async () => {
    if (user?.id) {
      await fetchProfileFlags(user.id);
    }
  }, [user?.id, fetchProfileFlags]);

  // Apply referral code from localStorage to profile (for OAuth signups)
  const applyStoredReferralCode = useCallback(async (userId: string) => {
    const code = localStorage.getItem('referrer_id');
    if (!code) return;

    try {
      const { data } = await supabase
        .from('profiles')
        .select('referred_by')
        .eq('id', userId)
        .single();

      if (data && !data.referred_by) {
        await supabase
          .from('profiles')
          .update({ referred_by: code.toUpperCase() })
          .eq('id', userId);
        console.log('[Affiliate] OAuth referral code applied:', code);
      }

      localStorage.removeItem('referrer_id');
    } catch (err) {
      console.error('[Affiliate] Error applying stored referral code:', err);
    }
  }, []);

  // Persist first-touch UTM attribution onto the profile so the Stripe
  // webhook can attribute Credit Purchased back to the cold-email campaign.
  // Uses set-once semantics: only writes if the profile has no campaign yet,
  // matching Mixpanel's people.set_once for first-touch attribution.
  const applyStoredUtmAttribution = useCallback(async (userId: string) => {
    const utm = getStoredUtmAttribution();
    if (!utm) return;

    try {
      const { data } = await supabase
        .from('profiles')
        .select('utm_campaign')
        .eq('id', userId)
        .single();

      if (data && !data.utm_campaign) {
        await supabase
          .from('profiles')
          .update({
            utm_source:   utm.utm_source   ?? null,
            utm_medium:   utm.utm_medium   ?? null,
            utm_campaign: utm.utm_campaign ?? null,
            utm_content:  utm.utm_content  ?? null,
          })
          .eq('id', userId);
        console.log('[Attribution] UTM persisted to profile:', utm.utm_campaign);
      }
      clearStoredUtmAttribution();
    } catch (err) {
      console.error('[Attribution] Error persisting UTM:', err);
    }
  }, []);

  // Initialize auth state
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user?.id) {
        // await so isAdmin is settled before isLoading flips false —
        // otherwise AdminRoute can race-trigger access-denied on page reload.
        await fetchProfileFlags(session.user.id);
        fetchAppSettings();
        updateLastActive(session.user.id);
        const p = session.user.app_metadata?.provider ?? session.user.identities?.[0]?.provider ?? 'email';
        trackKlaviyoLoginOncePerSession(p);
        applyStoredReferralCode(session.user.id);
        applyStoredUtmAttribution(session.user.id);
      }
      setIsLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user?.id) {
          fetchProfileFlags(session.user.id);
          fetchAppSettings();
          updateLastActive(session.user.id);
          identifyUser({
            id: session.user.id,
            email: session.user.email,
            name: session.user.user_metadata?.full_name ?? session.user.email,
          });
          if (event === 'SIGNED_IN') {
            applyStoredReferralCode(session.user.id);
            applyStoredUtmAttribution(session.user.id);
            const provider = session.user.app_metadata?.provider
              ?? session.user.identities?.[0]?.provider
              ?? 'email';
            // Detect first-time OAuth signup: created_at ~= last_sign_in_at (within 10s)
            const createdAt = new Date(session.user.created_at).getTime();
            const lastSignIn = new Date(session.user.last_sign_in_at ?? '').getTime();
            const isNewOAuthUser = provider !== 'email' && Math.abs(createdAt - lastSignIn) < 10000;
            if (isNewOAuthUser) {
              track('Sign Up', { signup_method: provider });
            }
            // Klaviyo-Signup-Sync: einmal pro Nutzer beim ersten Login eines
            // frisch angelegten Accounts. Deckt E-Mail-Bestätigungs-Signups ab
            // (dort schlägt der invoke bei signUp() mangels Session fehl) UND
            // OAuth-Erstlogins. Der localStorage-Guard verhindert Wiederholungen.
            if (Date.now() - createdAt < KLAVIYO_SIGNUP_SYNC_WINDOW_MS) {
              void syncSignupToKlaviyo(session.user.id);
            }
            track('Sign In', { login_method: provider });
            trackKlaviyoLoginOncePerSession(provider);
          }
        } else {
          setIsAdmin(false);
          setIsOnboarded(true);
          setDisabledFeatures([]);
          setProfileFlagsLoaded(true); // no profile to fetch when signed-out
          resetUser();
        }
        setIsLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, [fetchProfileFlags, fetchAppSettings, updateLastActive, applyStoredReferralCode, applyStoredUtmAttribution]);

  // Sign in with email/password
  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      return { error: error.message };
    }

    return { error: null };
  }, []);

  // Sign up with email/password
  const signUp = useCallback(async (email: string, password: string, referralCode?: string) => {
    // Priority: explicit referralCode param (from form) > localStorage (from URL)
    const code = referralCode?.trim() || localStorage.getItem('referrer_id') || null;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          referred_by: code ? code.toUpperCase() : null,
        },
      },
    });

    if (error) {
      return { error: error.message };
    }

    // Clear referrer from localStorage after successful signup
    localStorage.removeItem('referrer_id');
    if (code) {
      console.log('[Affiliate] Referral code applied and cleared:', code);
    }

    track('Sign Up', { signup_method: 'email', referral_code: code ?? undefined });
    // Klaviyo-Sync passiert NICHT hier: bei aktiver E-Mail-Bestätigung existiert
    // an dieser Stelle noch keine Session (der invoke liefe ins 401). Stattdessen
    // synchronisiert der SIGNED_IN-Handler beim ersten authentifizierten Login
    // (siehe syncSignupToKlaviyo) — deckt sowohl Bestätigungs- als auch
    // Auto-Confirm-Signups ab.
    return { error: null };
  }, []);

  // Sign in with OAuth provider (Google, Apple, Microsoft)
  const signInWithProvider = useCallback(async (provider: 'google') => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: window.location.origin,
      },
    });

    if (error) {
      return { error: error.message };
    }

    return { error: null };
  }, []);

  // Sign out
  const signOut = useCallback(async () => {
    resetUser();
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsAdmin(false);
    setIsOnboarded(true);
    setDisabledFeatures([]);
  }, []);

  const value: AuthContextType = {
    user,
    session,
    isLoading,
    profileFlagsLoaded,
    isAdmin,
    isOnboarded,
    signIn,
    signUp,
    signInWithProvider,
    signOut,
    refreshAdminStatus,
    refreshOnboardingStatus,
    disabledFeatures,
    refreshFeatureFlags,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ============================================================================
// Hook
// ============================================================================

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

